import type { Ancestor, EditorAboveOptions, Location, NodeEntry, NodeMatch } from 'slate';
import { Editor, Node, Path, Text } from 'slate';

import type { NodeEntryMatch } from '../internal';
import { Key, NODE_TO_KEY } from '../internal';

export interface AtOrAboveOptions<T extends Node> extends Omit<
  EditorAboveOptions<T & Ancestor>,
  'match'
> {
  match: NodeMatch<T>;
}

const nodeQueries = {
  /**
   * Similar to Editor.above, but can also match the node at the given location.
   */
  atOrAbove<T extends Node>(
    editor: Editor,
    options: AtOrAboveOptions<T>
  ): NodeEntry<T> | undefined {
    const { at = editor.selection, match, mode = 'lowest' } = options;
    if (!at) return;

    const atPath = Editor.path(editor, at);
    const atNode = Node.get(editor, atPath);
    const atNodeMatches = match(atNode, atPath);

    if (atNodeMatches && mode === 'lowest') return [atNode as T, atPath];

    const aboveEntry = Editor.above(editor, options) as NodeEntry<T> | undefined;
    if (aboveEntry) return aboveEntry;

    return atNodeMatches ? [atNode as T, atPath] : undefined;
  },

  /**
   * Find a key for a Slate node.
   */
  findKey(node: Node): Key {
    const cachedKey = NODE_TO_KEY.get(node);
    if (!cachedKey) {
      const newKey = new Key();
      NODE_TO_KEY.set(node, newKey);

      return newKey;
    }

    return cachedKey;
  },

  /**
   * Returns a match function that matches the provided path.
   */
  getMatchPath(editor: Editor, path: Path): (node: Node) => boolean {
    const node = Node.get(editor, path);
    return n => n === node;
  },

  /**
   * Get the next node entry at a Path, if it exists.
   */
  getNextNodeEntry(editor: Editor, path: Path): NodeEntry | null {
    const nextPath = Path.next(path);
    const nextNode = Node.getIf(editor, nextPath);
    if (!nextNode) return null;
    return [nextNode, nextPath];
  },

  /**
   * Get the previous node entry at a Path, if it exists.
   */
  getPreviousNodeEntry(editor: Editor, path: Path): NodeEntry | null {
    if (!Path.hasPrevious(path)) return null;
    const prevPath = Path.previous(path);
    const prevNode = Node.getIf(editor, prevPath);
    if (!prevNode) return null;
    return [prevNode, prevPath];
  },

  /**
   * Check if the selection is entirely contained within the node at the given path AND there is no
   * descendant of that node which also contains the entire selection and matches a query.
   *
   * For example, use this to check if a dynamic block is the lowest selected dynamic block.
   */
  isLowestSelectedNodeOfType(
    editor: Editor,
    options: {
      match: NodeEntryMatch;
      path: Path;
    }
  ) {
    const { match, path } = options;
    const nodeEntry = Editor.above(editor, { match });
    if (!nodeEntry) return false;
    return Path.equals(path, nodeEntry[1]);
  },

  /**
   * Similar implementation to Slate's Editor.above, but allows NodeEntryMatch vs. NodeMatch.
   */
  matchAbove<T extends Ancestor>(
    editor: Editor,
    options: {
      at?: Location;
      match?: NodeEntryMatch;
      mode?: 'highest' | 'lowest';
      voids?: boolean;
    } = {}
  ): NodeEntry<T> | void {
    const { at = editor.selection, match = () => true, mode = 'lowest', voids = false } = options;
    if (!at) return;

    const path = Editor.path(editor, at);
    const reverse = mode === 'lowest';

    for (const [n, p] of Editor.levels<T>(editor, { at: path, reverse, voids })) {
      if (!Text.isText(n) && !Path.equals(path, p) && match(n, p)) return [n, p];
    }
  },
};

export default nodeQueries;
